import { supabase } from './supabaseClient';

export const submissionService = {
  // --- Draft authoring (owner-side, plain table writes under RLS) ---
  async createDraft({ userId, type, period, periodStart, periodEnd, kpiSystemId = null, piSystemId = null, reportSystemId = null, countryId = null }) {
    const { data, error } = await supabase
      .from('submissions')
      .insert([{
        user_id: userId,
        type,
        period,
        period_start: periodStart,
        period_end: periodEnd,
        kpi_system_id: kpiSystemId,
        pi_system_id: piSystemId,
        report_system_id: reportSystemId,
        country_id: countryId,
        status: 'draft',
      }])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateDraft(submissionId, fields) {
    const { data, error } = await supabase
      .from('submissions')
      .update({ ...fields, updated_at: new Date() })
      .eq('id', submissionId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async getSubmission(submissionId) {
    const { data, error } = await supabase
      .from('submissions')
      .select('*')
      .eq('id', submissionId)
      .single();
    if (error) throw error;
    return data;
  },

  // The current user's submissions (RLS returns only own rows). The page splits
  // them into Drafts (draft/rejected) and Submitted (submitted/approved).
  async listMySubmissions() {
    const { data, error } = await supabase
      .from('submissions')
      .select('*, kpi_systems(name), pi_systems(name), report_systems(name), countries(name)')
      .order('updated_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async deleteDraft(submissionId) {
    const { error } = await supabase
      .from('submissions')
      .delete()
      .eq('id', submissionId);
    if (error) throw error;
  },

  async recentPeriods(period, count = 4) {
    const { data, error } = await supabase.rpc('recent_submission_periods', { p_period: period, p_count: count });
    if (error) throw error;
    return data; // [{ period_start, period_end }]
  },

  // Review queue. RLS (submissions_select_reviewer) scopes rows to the caller:
  // Supervisors see their assignees + assigned-country users; Admin/Manager see all.
  // submissions has TWO FKs to users (user_id, reviewed_by), so the submitter embed
  // MUST disambiguate via the constraint name to avoid PGRST201.
  async listForReview(status) {
    const orderCol = status === 'submitted' ? 'submitted_at' : 'reviewed_at';
    const { data, error } = await supabase
      .from('submissions')
      .select('*, kpi_systems(name), pi_systems(name), report_systems(name), countries(name), submitter:users!submissions_user_id_fkey(name, country_id, countries(name), team_lead:users!team_lead_id(id, name))')
      .eq('status', status)
      .order(orderCol, { ascending: false });
    if (error) throw error;
    return data;
  },

  // --- State transitions (server-side RPCs) ---
  async submit(submissionId) {
    const { data, error } = await supabase.rpc('submit_submission', { p_id: submissionId });
    if (error) throw error;
    return data;
  },

  async approve(submissionId) {
    const { data, error } = await supabase.rpc('approve_submission', { p_id: submissionId });
    if (error) throw error;
    return data;
  },

  async reject(submissionId, comment) {
    const { data, error } = await supabase.rpc('reject_submission', { p_id: submissionId, p_comment: comment });
    if (error) throw error;
    return data;
  },

  async disapprove(submissionId, comment) {
    const { data, error } = await supabase.rpc('disapprove_submission', { p_id: submissionId, p_comment: comment });
    if (error) throw error;
    return data;
  },

  // For the admin soft-warning: how many existing submissions exist per period
  // for one system. systemType is 'kpi' | 'performance' | 'report'.
  async getPeriodCountsForSystem(systemType, systemId) {
    const col = systemType === 'performance' ? 'pi_system_id'
      : systemType === 'report' ? 'report_system_id' : 'kpi_system_id';
    const { data, error } = await supabase
      .from('submissions')
      .select('period')
      .eq('type', systemType)
      .eq(col, systemId);
    if (error) throw error;
    return (data || []).reduce((acc, r) => { acc[r.period] = (acc[r.period] || 0) + 1; return acc; }, {});
  },
};
